import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Stepper, Step, StepLabel, Alert, CircularProgress, Button } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import FoodParcelSelector from '../components/order/FoodParcelSelector';
import OrderConfiguration from '../components/order/OrderConfiguration';
import CustomerDetails from '../components/order/CustomerDetails';
import CheckoutPage from '../components/order/CheckoutPage';
import OrderConfirmation from '../components/order/OrderConfirmation';
import { OrderData, CustomerDetails as ICustomerDetails, OrderConfiguration as IOrderConfiguration } from '../components/order/OrderData';
import { orderService } from '../services/order';
import { parcelDaySelectionApi } from '../services/parcel-day-selection';
import { orderPersistenceService } from '../services/orderPersistence';

const OrderPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state as any;

  // Return from BPOINT redirect: backend sends user to /order?payment=success|cancelled|failed&order_id=...
  const paymentReturn = searchParams.get('payment');
  const isPaymentReturn = paymentReturn === 'success' || paymentReturn === 'cancelled' || paymentReturn === 'failed';
  // When we're in a popup (opened by checkout), notify opener and close instead of running full order flow
  const isPopupReturn = typeof window !== 'undefined' && !!window.opener && isPaymentReturn;

  const [activeStep, setActiveStep] = useState(0);
  const [orderData, setOrderData] = useState<OrderData>({
    foodParcel: null,
    orderConfiguration: null,
    assignedParcelDay: null,
    customerDetails: null,
    orderId: undefined,
    orderNumber: undefined,
  });
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [canPlaceOrders, setCanPlaceOrders] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Scroll to top when moving between order steps (Customer Details / Configuration / Checkout)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  // Popup return: notify checkout window and close so user stays on checkout.
  // After BPOINT redirects here, we load in the popup; we postMessage to opener then close.
  // Short delay so postMessage is delivered before close; some browsers null opener after cross-origin nav.
  useEffect(() => {
    if (!isPopupReturn) return;
    const payment = searchParams.get('payment');
    const orderId = searchParams.get('order_id');
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          { type: 'bpoint_payment_return', payment, order_id: orderId },
          window.location.origin
        );
      } catch (e) {
        console.warn('OrderPage popup: postMessage failed', e);
      }
      // Brief delay so opener receives the message before we close
      const t = setTimeout(() => {
        window.close();
      }, 150);
      return () => clearTimeout(t);
    }
    // Opener missing or closed (e.g. user closed checkout tab): just close this popup if we can
    const t = setTimeout(() => window.close(), 200);
    return () => clearTimeout(t);
  }, [isPopupReturn, searchParams]);

  // Initialize order state. Entry rules (per NLFB flow):
  // - Direct /order (typed URL, new tab, incognito): if no session, redirect to /food-parcels.
  // - From Food Parcels (state.foodParcel): new order with that parcel, step 1.
  // - At /order with session (refresh, explicit resume, or return from BPOINT): recover from session/database.
  useEffect(() => {
    if (isPopupReturn) return;

    const initializeOrder = async () => {
      setLoading(true);

      const fromFoodParcels = !!state?.foodParcel;
      const explicitResume = state?.resume === true;
      const hasPaymentReturn = isPaymentReturn;

      if (!recoveryAttempted) {
        setRecoveryAttempted(true);

        if (fromFoodParcels) {
          orderPersistenceService.clearSession();
          setOrderData(prev => ({
            ...prev,
            foodParcel: state.foodParcel,
            assignedParcelDay: state.assignedParcelDay ?? null,
            orderConfiguration: null,
            customerDetails: null,
            orderId: undefined,
            orderNumber: undefined,
          }));
          setActiveStep(1);
          setError(null);
          setRecoveryMessage(null);
          setDataLoaded(true);
        } else {
          // No state from food parcels: try to recover from session (covers refresh, explicit resume, BPOINT return)
          try {
            const recoveryData = await orderPersistenceService.recoverOrderState();
            if (recoveryData.canRecover) {
              setOrderData(recoveryData.orderData as OrderData);

              // If restoring to checkout (step 3), ensure order status is on_hold or processing.
              // Backend submit rejects status 'created'; redirect to configuration if needed.
              let stepToShow = recoveryData.currentStep;
              if (recoveryData.currentStep === 3 && recoveryData.orderData?.orderId) {
                try {
                  const orderRes = await orderService.getOrder(recoveryData.orderData.orderId);
                  const order = orderRes?.data?.data ?? orderRes?.data ?? orderRes;
                  if (order?.status === 'created') {
                    stepToShow = 2;
                    setActiveStep(2);
                    setRecoveryMessage('Please complete your order configuration again.');
                    setTimeout(() => setRecoveryMessage(null), 8000);
                  } else {
                    setActiveStep(recoveryData.currentStep);
                  }
                } catch (e) {
                  console.warn('OrderPage: Could not verify order status for checkout recovery', e);
                  setActiveStep(recoveryData.currentStep);
                }
              } else {
                setActiveStep(recoveryData.currentStep);
              }

              if (recoveryData.currentStep > 0) {
                setError(null);
                if (!hasPaymentReturn && stepToShow === recoveryData.currentStep) {
                  setRecoveryMessage(`Order recovered! You were on step ${recoveryData.currentStep + 1}. Your previous data has been restored.`);
                  setTimeout(() => setRecoveryMessage(null), 5000);
                }
              }
              setDataLoaded(true);
            } else {
              // No session / can't recover: treat as direct access, send to food parcels
              orderPersistenceService.clearSession();
              navigate('/food-parcels', { replace: true });
              return;
            }
          } catch (recoveryError) {
            console.warn('OrderPage: Recovery failed, redirecting to food parcels', recoveryError);
            orderPersistenceService.clearSession();
            navigate('/food-parcels', { replace: true });
            return;
          }
        }
      }

      try {
        // Check if orders can be placed (for messaging; direct-access step 0 is already set above)
        const canPlace = await parcelDaySelectionApi.canPlaceOrders();
        setCanPlaceOrders(canPlace);

        if (!canPlace) {
          setError('Orders are currently closed. Please check the schedule for when ordering will be available.');
        }
      } catch (err) {
        console.error('Failed to initialize order:', err);
        setError('Unable to initialize order. Please try again.');
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    };

    initializeOrder();
  }, [isPopupReturn, state?.foodParcel, state?.resume, state?.assignedParcelDay, recoveryAttempted, isPaymentReturn]);

  // Auto-assign parcel day if not already assigned
  useEffect(() => {
    const autoAssignParcelDay = async () => {
      if (state?.foodParcel && !state?.assignedParcelDay && state?.autoAssignParcelDay) {
        try {
          const assignedParcelDay = await parcelDaySelectionApi.assignNextAvailableDay();
          setOrderData(prev => ({
            ...prev,
            assignedParcelDay
          }));
        } catch (err) {
          console.error('Failed to auto-assign parcel day:', err);
        }
      }
    };

    autoAssignParcelDay();
  }, [state?.foodParcel, state?.assignedParcelDay, state?.autoAssignParcelDay]);

  const handleNext = () => {
    // Check if orders can be placed
    if (canPlaceOrders === false) {
      setError('Orders are currently closed. Please check the schedule for when ordering will be available.');
      return;
    }

    // Validate current step before proceeding
    const canProceed = (() => {
      console.log('OrderPage: Validating step', activeStep, {
        hasFoodParcel: !!orderData.foodParcel,
        hasCustomerDetails: !!orderData.customerDetails,
        hasOrderConfiguration: !!orderData.orderConfiguration
      });
      
      switch (activeStep) {
        case 0: // Food Parcel Selection
          return orderData.foodParcel !== null;
        case 1: // Customer Details
          return orderData.foodParcel !== null;
        case 2: // Order Configuration
          return orderData.foodParcel !== null && orderData.customerDetails !== null;
        case 3: // Checkout
          return orderData.foodParcel !== null && orderData.customerDetails !== null && orderData.orderConfiguration !== null;
        case 4: // Order Confirmation
          return true; // Always allow proceeding to confirmation
        default:
          return true;
      }
    })();

    if (!canProceed) {
      console.log('OrderPage: Cannot proceed from step', activeStep, 'due to missing data');
      setError('Please complete the current step before proceeding.');
      return;
    }

    const newStep = activeStep + 1;
    setActiveStep(newStep);
    setError(null); // Clear any previous errors
    
    // Save current state to session
    orderPersistenceService.saveToSession({
      currentStep: newStep,
      orderId: orderData.orderId,
      orderNumber: orderData.orderNumber,
      sessionData: orderData
    });
  };

  const handleBack = () => {
    const newStep = activeStep - 1;
    setActiveStep(newStep);
    
    // Save current state to session
    orderPersistenceService.saveToSession({
      currentStep: newStep,
      orderId: orderData.orderId,
      orderNumber: orderData.orderNumber,
      sessionData: orderData
    });
  };

  const handleCancelOrder = async () => {
    setCancelling(true);
    setError(null);
    try {
      if (orderData.orderId != null) {
        await orderService.cancelOrder(orderData.orderId);
      }
      orderPersistenceService.clearSession();
      navigate('/food-parcels', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to cancel order. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleUpdateOrderData = (step: keyof OrderData, data: any) => {
    const newOrderData = {
      ...orderData,
      [step]: data,
    };
    
    setOrderData(newOrderData);
    
    // Update session with new data
    orderPersistenceService.updateSessionOrderData(newOrderData);
  };

  const handleCreateOrder = async (customerData: ICustomerDetails): Promise<void> => {
    try {
      const currentOrderData = { 
        ...orderData, 
        customerDetails: customerData,
      };
      console.log('OrderPage - customerData:', customerData);
      console.log('OrderPage - currentOrderData:', currentOrderData);
      
      const response = await orderService.createOrder(currentOrderData);
      const newOrderData = {
        ...orderData,
        customerDetails: customerData,
        orderId: response.data.order.id,
        orderNumber: response.data.order.order_number,
        totalAmount: response.data.total_amount
      };
      
      setOrderData(newOrderData);
      
      // Save to session after order creation
      orderPersistenceService.saveToSession({
        currentStep: 2, // Move to configuration step
        orderId: response.data.order.id,
        orderNumber: response.data.order.order_number,
        sessionData: newOrderData
      });
      
      console.log('OrderPage: Saved to session after order creation', {
        orderId: response.data.order.id,
        hasCustomerDetails: !!newOrderData.customerDetails
      });
      
      // Don't call handleNext() here - let the CustomerDetails component handle navigation
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create order.');
      throw err; // Re-throw the error so CustomerDetails can handle it
    }
  };

  const handleUpdateConfiguration = async (configData: IOrderConfiguration): Promise<void> => {
    if (!orderData.orderId) {
      setError('Order ID is missing. Cannot update configuration.');
      return;
    }
    try {
      // Transform the configuration data to match backend expectations
      const backendConfiguration = {
        bundle_selections: configData.configuration.bundle_selections,
        bread_selection: configData.configuration.bread_selection,
        grocery_selections: configData.configuration.grocery_selections,
        parcel_item_selections: configData.configuration.parcel_item_selections,
        addon_product_selections: configData.configuration.addon_product_selections ?? {},
      };
      
      const response = await orderService.updateConfiguration(orderData.orderId, backendConfiguration);
      
      // Fetch the updated order data from the backend
      const orderResponse = await orderService.getOrder(orderData.orderId);
      
      console.log('OrderPage - orderResponse:', orderResponse);
      console.log('OrderPage - orderResponse.data:', orderResponse.data);
      console.log('OrderPage - orderResponse.data.data:', orderResponse.data.data);
      
      const newOrderData = {
        ...orderData,
        orderConfiguration: configData,
        order: orderResponse.data.data, // The order data is wrapped in a 'data' property
        totalAmount: response.data.total_amount
      };
      
      setOrderData(newOrderData);
      console.log('OrderPage - new order data:', newOrderData);
      
      // Save to session after configuration update
      orderPersistenceService.saveToSession({
        currentStep: 3, // Move to checkout step
        orderId: orderData.orderId,
        orderNumber: orderData.orderNumber,
        sessionData: newOrderData
      });
      
      // Don't call handleNext() here - let the OrderConfiguration component handle navigation
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update order configuration.');
      throw err; // Re-throw the error so OrderConfiguration can handle it
    }
  };

  const handleCheckoutSubmit = async (checkoutData: any) => {
    const orderId = orderData.orderId ?? orderData.order?.id;
    if (!orderId) {
      setError('Order ID is missing. Cannot submit order.');
      return;
    }

    const setSubmitError = (err: any, defaultMsg: string) => {
      // No HTTP response (network, CORS, wrong URL) – show actionable message
      if (!err.response) {
        setError('Connection or server error. Check your connection and the browser Network tab, then try again.');
        return;
      }
      const data = err.response.data;
      const msg = data?.message || defaultMsg;
      const backendError = data?.error;
      const validationErrors = data?.errors;
      // Non-JSON or empty message – include status so tester/user has a clue
      if (!data?.message && !backendError && !(validationErrors && typeof validationErrors === 'object')) {
        const status = err.response.status;
        setError(status ? `Server error (HTTP ${status}). Please try again or contact support.` : defaultMsg);
        return;
      }
      if (backendError) {
        setError(`${msg} ${typeof backendError === 'string' ? backendError : JSON.stringify(backendError)}`.trim());
      } else if (validationErrors && typeof validationErrors === 'object') {
        const first = Object.values(validationErrors).flat()[0];
        setError(first ? `${String(first)}` : msg);
      } else {
        setError(msg);
      }
    };

    try {
      // Update customer details first (separate error so we know which step failed)
      if (checkoutData.customer_details) {
        const customerDetailsWithDeliveryMethod = {
          ...(orderData.customerDetails ?? {}),
          ...checkoutData.customer_details,
          delivery_method: checkoutData.delivery_method,
        };
        try {
          // Log so we can tell "submit never hit" from "customer-details failed" (see backend log)
          if (process.env.NODE_ENV === 'development') {
            console.log('[OrderPage] Submitting customer details', { orderId });
          }
          await orderService.updateCustomerDetails(orderId, customerDetailsWithDeliveryMethod);
          setOrderData(prev => ({
            ...prev,
            customerDetails: customerDetailsWithDeliveryMethod,
          }));
        } catch (err: any) {
          setSubmitError(err, 'Failed to update customer details.');
          throw err;
        }
      }

      // Submit the order (separate error so we know if submit failed)
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log('[OrderPage] Submitting order', { orderId });
        }
        await orderService.submitOrder(orderId, checkoutData);
      } catch (err: any) {
        setSubmitError(err, 'Failed to submit order.');
        throw err;
      }

      // Fetch full order so confirmation can show selected pickup timeslot (not order time)
      const orderResponse = await orderService.getOrder(orderId);
      const submittedOrder = orderResponse?.data?.data ?? orderResponse?.data;

      // Update order data with delivery method, fee, and full order (includes parcel_day_pickup_slot)
      setOrderData(prev => ({
        ...prev,
        deliveryMethod: checkoutData.delivery_method,
        deliveryFee: checkoutData.delivery_fee,
        order: submittedOrder,
      }));

      // Clear session after successful order submission
      orderPersistenceService.clearSession();

      handleNext();
    } catch (err: any) {
      // setSubmitError already called for customer-details or submit; for any other throw (e.g. getOrder) set fallback
      setSubmitError(err, 'Something went wrong. Please try again.');
      throw err;
    }
  };

  const getStepContent = (step: number) => {
    // Don't render step content until data is loaded
    if (!dataLoaded) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }
    
    switch (step) {
      case 0:
        return (
          <FoodParcelSelector
            onNext={handleNext}
            onSelect={(data) => handleUpdateOrderData('foodParcel', data)}
            onParcelDayAssigned={(data) => handleUpdateOrderData('assignedParcelDay', data)}
            onCancel={handleCancelOrder}
            cancelling={cancelling}
          />
        );
      case 1:
        if (!orderData.foodParcel) {
          return (
            <Alert severity="error">
              No food parcel selected. Please go back and select a food parcel.
            </Alert>
          );
        }
        return (
          <CustomerDetails
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={handleCreateOrder}
            onCancel={handleCancelOrder}
            cancelling={cancelling}
          />
        );
      case 2:
        console.log('OrderPage: Rendering step 2 (Configuration)', {
          hasFoodParcel: !!orderData.foodParcel,
          hasCustomerDetails: !!orderData.customerDetails,
          customerDetails: orderData.customerDetails
        });
        
        if (!orderData.foodParcel) {
          return (
            <Alert severity="error">
              No food parcel selected. Please go back and select a food parcel.
            </Alert>
          );
        }
        if (!orderData.customerDetails) {
          console.log('OrderPage: No customer details found, showing error');
          return (
            <Alert severity="error">
              Customer details not available. Please go back and complete the customer details step.
            </Alert>
          );
        }
        return (
          <OrderConfiguration
            foodParcel={orderData.foodParcel}
            assignedParcelDay={orderData.assignedParcelDay}
            onNext={handleNext}
            onBack={handleBack}
            onConfiguration={handleUpdateConfiguration}
            onCancel={handleCancelOrder}
            cancelling={cancelling}
          />
        );
      case 3:
        if (!orderData.foodParcel) {
          return (
            <Alert severity="error">
              No food parcel selected. Please go back and select a food parcel.
            </Alert>
          );
        }
        if (!orderData.customerDetails) {
          return (
            <Alert severity="error">
              Customer details not available. Please go back and complete the customer details step.
            </Alert>
          );
        }
        if (!orderData.orderConfiguration) {
          return (
            <Alert severity="error">
              Order configuration not available. Please go back and complete the order configuration step.
            </Alert>
          );
        }
        return (
          <CheckoutPage
            orderData={orderData as any}
            onBack={handleBack}
            onSubmit={handleCheckoutSubmit}
            onCancel={handleCancelOrder}
            cancelling={cancelling}
          />
        );
      case 4:
        return <OrderConfirmation orderData={orderData as any} />;
      default:
        return 'Unknown step';
    }
  };

  // Dynamic steps array - exclude "Select Food Parcel" if starting from step 1
  const getSteps = () => {
    if (activeStep > 0) {
      return ['Customer Details', 'Configure Order', 'Checkout', 'Order Confirmation'];
    }
    return ['Select Food Parcel', 'Customer Details', 'Configure Order', 'Checkout', 'Order Confirmation'];
  };

  const steps = getSteps();

  // Popup return: show brief message while we postMessage and close
  if (isPopupReturn) {
    const payment = searchParams.get('payment');
    const msg =
      payment === 'success'
        ? 'Payment received. Closing...'
        : payment === 'cancelled'
          ? 'Payment cancelled. Closing...'
          : 'Payment did not complete. Closing...';
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography>{msg}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            If this window does not close, you can close it yourself.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Place Your Order
      </Typography>
      
      {orderData.orderNumber && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            Order #{orderData.orderNumber}
          </Typography>
        </Box>
      )}
      
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      
      {recoveryMessage && (
        <Alert severity="success" onClose={() => setRecoveryMessage(null)} sx={{ mb: 2 }}>
          {recoveryMessage}
        </Alert>
      )}
      
      {!loading && canPlaceOrders === false && !state?.foodParcel && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Orders Currently Closed
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            No orders can be placed at this time. Please check the schedule for when ordering will be available.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.href = '/food-parcels'}
            sx={{ mt: 1 }}
          >
            Go to Food Parcels Page
          </Button>
        </Alert>
      )}
      
      {!loading && canPlaceOrders !== false && (
        <>
          <Box sx={{ width: '100%', mb: 4 }}>
            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Box>{getStepContent(activeStep)}</Box>
        </>
      )}
    </Container>
  );
};

export default OrderPage; 